import { KeyboardArrowUp, LinkedIn, Telegram } from "@mui/icons-material";
import { Box, Card, IconButton, Link, Typography } from "@mui/joy";
import { useState } from "react";

import type { TeamMember } from "../../AboutUs";

export const TeamMemberCard = ({ member }: { member: TeamMember }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      variant="outlined"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        p: 0,
        overflow: "hidden",
        bgcolor: "background.surface",
        borderColor: "divider",
        transition: "all 0.3s ease",
        position: "relative",
        minHeight: { xs: 280, sm: 320, md: 360 },
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "primary.300",
          boxShadow: "md",
        },
      }}
    >
      {/* Photo area */}
      <Box
        sx={{
          height: { xs: 180, sm: 200, md: 220 },
          bgcolor: "neutral.100",
          "[data-joy-color-scheme='dark'] &": {
            bgcolor: "neutral.800",
          },
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={member.photoUrl}
          alt={member.name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center center",
          }}
        />
      </Box>

      {/* Content area */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flex: 1,
        }}
      >
        {/* Name and Social Icons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            level="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {member.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {member.telegram && (
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                component="a"
                href={member.telegram}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.500",
                  "&:hover": {
                    bgcolor: "primary.50",
                    "[data-joy-color-scheme='dark'] &": {
                      bgcolor: "primary.900",
                    },
                  },
                }}
              >
                <Telegram />
              </IconButton>
            )}
            {member.linkedIn && (
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                component="a"
                href={member.linkedIn}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.500",
                  "&:hover": {
                    bgcolor: "primary.50",
                    "[data-joy-color-scheme='dark'] &": {
                      bgcolor: "primary.900",
                    },
                  },
                }}
              >
                <LinkedIn />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Position */}
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: { xs: "13px", sm: "14px" },
          }}
        >
          {member.position}
        </Typography>

        {/* More info text */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: "auto",
            pt: 1,
            color: "primary.500",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: "13px", sm: "14px" },
              color: "inherit",
            }}
          >
            Більше про {member.moreAboutName}
          </Typography>
          <KeyboardArrowUp sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      {/* Expandable overlay on hover */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "background.surface",
          borderTop: "1px solid",
          borderColor: "divider",
          p: { xs: 2, sm: 2.5, md: 3 },
          transform: isHovered ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Name and Social Icons in expanded view */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            level="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            {member.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {member.telegram && (
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                component="a"
                href={member.telegram}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.500" }}
              >
                <Telegram />
              </IconButton>
            )}
            {member.linkedIn && (
              <IconButton
                variant="plain"
                color="primary"
                size="sm"
                component="a"
                href={member.linkedIn}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.500" }}
              >
                <LinkedIn />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Position */}
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: { xs: "13px", sm: "14px" },
            mb: 2,
          }}
        >
          {member.position}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "15px" },
            lineHeight: 1.7,
            color: "text.primary",
            flex: 1,
          }}
        >
          {member.description}
        </Typography>

        {/* Contacts section */}
        {member.email && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "13px", sm: "14px" },
                mb: 0.5,
              }}
            >
              Контакти
            </Typography>
            <Link
              href={`mailto:${member.email}`}
              sx={{
                fontSize: { xs: "13px", sm: "14px" },
                color: "primary.500",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {member.email}
            </Link>
          </Box>
        )}
      </Box>
    </Card>
  );
};
